import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { duration, flipFromTop } from "@/shared/lib";
import { tactile } from "@/shared/telegram";

import { Icon } from "./Icon";
import type { IconName } from "./icons/paths";
import { ToastContext, type ToastInput, type ToastTone } from "./toast-context";

/**
 * Тост. Figma: `State · Deposit Toast` (28:31) — 350×74, подложка в тон
 * статуса, рамка полным цветом, слева бейдж 36×36 с глифом.
 *
 * Тональности ровно две: `profit` и `loss`. Подобранные тёмные подложки в
 * макете существуют только для них (#173823 и #3a171e); для warning и info
 * их нет, и выдумывать оттенок ради полноты набора значило бы разойтись
 * с референсом.
 *
 * Движение (FE-13):
 *  - тост приезжает сверху и уходит туда же; пропадание без ухода читается
 *    как сбой отрисовки;
 *  - полоса внизу показывает, сколько ему осталось, — иначе исчезновение
 *    выглядит случайным;
 *  - когда верхний тост уходит, нижние не перескакивают, а переезжают (FLIP);
 *  - исход операции дублируется тактильно: успех и отказ ощущаются по-разному.
 */
interface Toast extends ToastInput {
  id: number;
  /** Уходит: доигрывает исходящую анимацию и через мгновение снимается. */
  leaving: boolean;
}

const TONES: Record<ToastTone, { box: string; badge: string; bar: string; icon: IconName }> = {
  profit: {
    box: "bg-surface-profit border-status-profit",
    badge: "bg-status-profit",
    bar: "bg-status-profit",
    icon: "check",
  },
  loss: {
    box: "bg-surface-loss border-status-loss",
    badge: "bg-status-loss",
    bar: "bg-status-loss",
    icon: "warningFill",
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const show = useCallback((toast: ToastInput) => {
    nextId.current += 1;
    const id = nextId.current;

    tactile(toast.tone === "profit" ? "success" : "error");
    setToasts((current) => [...current, { ...toast, id, leaving: false }]);

    const visible = duration("toast");
    const leaving = duration("fast");

    window.setTimeout(() => {
      setToasts((current) =>
        current.map((item) => (item.id === id ? { ...item, leaving: true } : item)),
      );
    }, visible);

    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, visible + leaving);
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-60 flex flex-col items-center gap-2 p-5 [view-transition-name:toasts]">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} />
        ))}
      </div>
    </ToastContext>
  );
}

function ToastCard({ toast }: { toast: Toast }) {
  const tone = TONES[toast.tone];
  const ref = useRef<HTMLDivElement>(null);
  const top = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    top.current = flipFromTop(element, top.current);
  });

  return (
    <div
      ref={ref}
      role="status"
      data-leaving={toast.leaving || undefined}
      className={[
        "relative flex w-full max-w-[350px] items-start gap-3 overflow-hidden rounded-[18px] border border-solid p-[14px]",
        "animate-toast-in data-[leaving]:animate-toast-out",
        tone.box,
      ].join(" ")}
    >
      <span className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${tone.badge}`}>
        <Icon name={tone.icon} size={18} className="text-text-inverse" />
      </span>
      <div className="flex flex-col gap-[3px]">
        <p className="text-label font-semibold text-text-primary">{toast.title}</p>
        {toast.meta ? <p className="text-micro font-normal text-text-secondary">{toast.meta}</p> : null}
      </div>

      <span
        aria-hidden="true"
        className={`animate-deplete absolute inset-x-0 bottom-0 h-0.5 origin-left opacity-40 ${tone.bar}`}
      />
    </div>
  );
}
